import React from 'react';
import { motion } from 'framer-motion';
import { Fence, Route, Zap, Droplets, TreePine, DoorOpen } from 'lucide-react';
import { useLanguage } from '@/context/LanguageContext';
import translations from '@/data/translations';
import { MEDIA } from '@/data/plotData';
import LanguageLogo from '@/components/Logo';
import { LANDING } from '@/constants/testIds';

export default function InfrastructureGrid() {
  const { lang } = useLanguage();
  const t = translations[lang].infra;

  return (
    <section id="infrastructure" data-testid={LANDING.infrastructure} className="py-16 sm:py-24 bg-[#F4F1EA]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-12 sm:mb-16"
        >
          <span className="text-[#D48C57] text-xs font-medium tracking-[0.2em] uppercase">
            {t.sectionTag}
          </span>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-[#1A2520] mt-3 tracking-tight">
            {t.title}
          </h2>
          <p className="text-[#5C6A61] text-base mt-3 max-w-2xl">
            {t.subtitle}
          </p>
        </motion.div>

        {/* Bento Grid */}
        <div className="grid grid-cols-4 sm:grid-cols-8 lg:grid-cols-12 gap-3 auto-rows-[120px] sm:auto-rows-[140px]">
          {/* Large Drone Image - Roads */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="col-span-4 sm:col-span-5 lg:col-span-8 row-span-2 rounded-2xl overflow-hidden relative group"
          >
            <img
              src={MEDIA.droneAerial}
              alt="Aerial view of Lakshmi Nivasa"
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
            <div className="absolute bottom-4 left-4">
              <p className="text-white font-serif text-lg font-bold">{t.roads}</p>
              <p className="text-white/70 text-xs mt-0.5">{t.roadsDesc}</p>
            </div>
          </motion.div>

          {/* Boundary Wall Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1, duration: 0.5 }}
            className="col-span-2 sm:col-span-3 lg:col-span-4 row-span-1 rounded-2xl bg-[#2C4C3B] p-5 flex flex-col justify-between"
          >
            <Fence className="w-6 h-6 text-[#D48C57]" />
            <div>
              <p className="text-white font-serif text-sm sm:text-base font-bold">{t.boundary}</p>
              <p className="text-white/60 text-[11px] mt-1 leading-relaxed hidden sm:block">{t.boundaryDesc}</p>
            </div>
          </motion.div>

          {/* Electrical Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.15, duration: 0.5 }}
            className="col-span-2 sm:col-span-3 lg:col-span-4 row-span-1 rounded-2xl bg-white border border-[#E5DFD3] p-5 flex flex-col justify-between"
          >
            <Zap className="w-6 h-6 text-[#D48C57]" />
            <div>
              <p className="text-[#1A2520] font-serif text-sm sm:text-base font-bold">{t.electrical}</p>
              <p className="text-[#5C6A61] text-[11px] mt-1 leading-relaxed hidden sm:block">{t.electricalDesc}</p>
            </div>
          </motion.div>

          {/* Signage Photo - Large card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.2, duration: 0.5 }}
            className="col-span-4 sm:col-span-4 lg:col-span-5 row-span-2 rounded-2xl overflow-hidden relative group"
          >
            <img
              src={MEDIA.signage}
              alt="Lakshmi Nivasa premium signage"
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            <div className="absolute bottom-4 left-4">
              <p className="text-white font-serif text-lg font-bold">{t.gate}</p>
              <p className="text-white/70 text-xs mt-0.5">{t.gateDesc}</p>
            </div>
          </motion.div>

          {/* Gate Access Photo - Smaller card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.22, duration: 0.5 }}
            className="col-span-2 sm:col-span-4 lg:col-span-3 row-span-2 rounded-2xl overflow-hidden relative group"
          >
            <img
              src={MEDIA.gateAccess}
              alt="Dual access gates at Lakshmi Nivasa"
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent" />
            <div className="absolute bottom-4 left-4">
              <LanguageLogo lang={lang} variant="white" size="sm" showTagline={false} />
            </div>
          </motion.div>

          {/* Groundwater Card */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.25, duration: 0.5 }}
            className="col-span-2 sm:col-span-4 lg:col-span-4 row-span-1 rounded-2xl bg-white border border-[#E5DFD3] p-5 flex flex-col justify-between"
          >
            <Droplets className="w-6 h-6 text-[#2C4C3B]" />
            <div>
              <p className="text-[#1A2520] font-serif text-sm sm:text-base font-bold">{t.water}</p>
              <p className="text-[#5C6A61] text-[11px] mt-1 leading-relaxed hidden sm:block">{t.waterDesc}</p>
            </div>
          </motion.div>

          {/* Oblique Drone View */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.28, duration: 0.5 }}
            className="col-span-2 sm:col-span-4 lg:col-span-4 row-span-1 rounded-2xl overflow-hidden relative group"
          >
            <img
              src={MEDIA.droneOblique}
              alt="Oblique view showing roads and layout"
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            <div className="absolute bottom-3 left-3">
              <p className="text-white font-serif text-sm font-bold">{t.roads}</p>
            </div>
          </motion.div>

          {/* Park Image */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3, duration: 0.5 }}
            className="col-span-4 sm:col-span-4 lg:col-span-4 row-span-1 rounded-2xl overflow-hidden relative group"
          >
            <img
              src={MEDIA.greenPark}
              alt="Park and green spaces"
              className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
              loading="lazy"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            <div className="absolute bottom-3 left-3">
              <p className="text-white font-serif text-sm font-bold">{t.park}</p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
